
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Share2, Check, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth";
import { toast } from "sonner";

interface ProjectShareButtonProps {
  projectId: string;
  projectName: string;
}

interface ProjectShare {
  id: string;
  shared_with_email: string;
  created_at: string;
  accepted: boolean;
}

const ProjectShareButton: React.FC<ProjectShareButtonProps> = ({
  projectId,
  projectName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sharedEmail, setSharedEmail] = useState("");
  const [activeTab, setActiveTab] = useState("share");
  const [existingShares, setExistingShares] = useState<ProjectShare[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const { user } = useAuth();

  // Fetch existing shares when dialog opens
  useEffect(() => {
    if (isOpen && user) {
      fetchExistingShares();
    }
  }, [isOpen, projectId, user]);

  const fetchExistingShares = async () => {
    if (!projectId) return;
    
    try {
      setIsLoadingShares(true);
      
      const { data, error } = await supabase
        .from("project_shares")
        .select("*")
        .eq("project_id", projectId)
        .eq("owner_id", user?.id);
      
      if (error) throw error;
      
      setExistingShares(data || []);
    } catch (err: any) {
      console.error("Error fetching project shares:", err);
      toast.error("Kunde inte hämta delningar: " + err.message);
    } finally {
      setIsLoadingShares(false);
    }
  };

  const handleShareProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    const trimmedEmail = email.toLowerCase().trim();
    
    // Validate email
    if (!trimmedEmail) {
      setError("Ange en e-postadress");
      return;
    }
    
    // Check that user is not sharing with themselves
    if (user?.email === trimmedEmail) {
      setError("Du kan inte dela ett projekt med dig själv");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Check if project is already shared with this email
      const { data: existingShares, error: checkError } = await supabase
        .from("project_shares")
        .select("id")
        .eq("project_id", projectId)
        .eq("shared_with_email", trimmedEmail);
      
      if (checkError) {
        throw new Error("Kunde inte kontrollera delningar: " + checkError.message);
      }
      
      if (existingShares && existingShares.length > 0) {
        setError(`Projektet är redan delat med ${trimmedEmail}`);
        return;
      }
      
      // Share the project
      const { data, error: shareError } = await supabase
        .from("project_shares")
        .insert({
          project_id: projectId,
          owner_id: user?.id,
          shared_with_email: trimmedEmail,
          permission_level: "read"
        })
        .select("id");
      
      if (shareError) {
        throw new Error("Kunde inte dela projektet: " + shareError.message);
      }

      // Automatically send email notification
      if (data && data[0]?.id) {
        try {
          const { error: emailError } = await supabase.functions.invoke(
            "send-project-notification",
            {
              body: {
                recipientEmail: trimmedEmail,
                projectName,
                ownerName: user?.full_name || user?.first_name || "En användare",
                shareId: data[0].id,
              },
            }
          );
          
          if (emailError) {
            console.warn("Email notification failed:", emailError);
            toast.warning("Projektet delades men e-postnotifiering kunde inte skickas");
          }
        } catch (emailError) {
          console.warn("Email notification error:", emailError);
        }
      }
      
      // Show success message
      setSharedEmail(trimmedEmail);
      setShowSuccess(true);
      toast.success(`Projektet delades med ${trimmedEmail}`);
      
      // Reset form
      setEmail("");
      fetchExistingShares();
    } catch (error: any) {
      console.error("Error sharing project:", error);
      setError(error.message || "Ett fel uppstod vid delning av projektet");
      toast.error(error.message || "Ett fel uppstod vid delning av projektet");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendNotification = async (shareId: string, email: string) => {
    try {
      toast.loading(`Skickar notifikation till ${email}...`);
      
      const { error } = await supabase.functions.invoke(
        "send-project-notification",
        {
          body: {
            recipientEmail: email,
            projectName,
            ownerName: user?.full_name || user?.first_name || "En användare",
            shareId,
          },
        }
      );

      if (error) {
        throw error;
      }
      
      toast.success(`Notifikation skickad till ${email}`);
    } catch (error: any) {
      console.error("Error sending notification:", error);
      toast.error("Kunde inte skicka notifikation: " + error.message);
    }
  };
  
  const handleClose = () => {
    setIsOpen(false);
    setShowSuccess(false);
    setError(null);
    setEmail("");
    setActiveTab("share");
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
      >
        <Share2 className="h-4 w-4" />
        Dela projekt
      </Button>
      
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {showSuccess ? "Delning slutförd" : `Dela projektet "${projectName}"`}
            </DialogTitle>
          </DialogHeader>
          
          {showSuccess ? (
            <div className="space-y-4 py-4 text-center">
              <div className="mx-auto rounded-full p-3 bg-green-50 w-14 h-14 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 h-6 w-6"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <h3 className="text-lg font-medium">Projektet har delats</h3>
              <p className="text-sm text-gray-500">
                Hela projektet <strong>"{projectName}"</strong> med alla mappar och riskbedömningar har delats med <strong>{sharedEmail}</strong>. 
              </p>
              <p className="text-sm text-gray-500 mt-2">
                De kommer att kunna se allt projektinnehåll nästa gång de loggar in och kommer att få en e-postnotifikation.
              </p>
              <div className="pt-4">
                <Button onClick={handleClose} className="w-full">
                  Stäng
                </Button>
              </div>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="share">Dela projekt</TabsTrigger>
                <TabsTrigger value="manage">Hantera delningar</TabsTrigger>
              </TabsList>
              
              <TabsContent value="share">
                <form onSubmit={handleShareProject} className="space-y-4 pt-2">
                  <div className="space-y-1">
                    <Label htmlFor="email">E-postadress</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Ange e-postadressen för att dela med"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2 p-3 bg-blue-50 rounded-md border">
                    <h4 className="text-sm font-medium text-blue-900">Vad som delas:</h4>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        Hela projektet med alla mappar och undermappar
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        Alla riskbedömningar och dokument
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        Läsbehörighet för allt innehåll
                      </li>
                    </ul>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex justify-end space-x-2 pt-2">
                    <Button variant="outline" type="button" onClick={handleClose}>
                      Avbryt
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Delar..." : "Dela projektet"}
                    </Button>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="manage">
                <div className="py-2">
                  <h3 className="text-sm font-medium mb-2">Nuvarande delningar</h3>
                  
                  {isLoadingShares ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                      <p className="text-sm text-gray-500 mt-2">Hämtar delningar...</p>
                    </div>
                  ) : existingShares.length === 0 ? (
                    <div className="text-center py-4 border rounded-md bg-gray-50">
                      <p className="text-sm text-gray-500">Projektet är inte delat med någon än</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {existingShares.map(share => (
                        <div key={share.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md border">
                          <div>
                            <p className="text-sm font-medium">{share.shared_with_email}</p>
                            <p className="text-xs text-gray-500">
                              Delad: {new Date(share.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs"
                            onClick={() => handleResendNotification(share.id, share.shared_with_email)}
                          >
                            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
                            Skicka igen
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" type="button" onClick={handleClose}>
                      Stäng
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectShareButton;
